// Languages — the i18n locales the platform renders in. Each country picks a default from here
// (managed on the country's Locale tab). Global list: add / edit / delete, search, paginate.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { focusModal } from '../shared/modal.js';
import { rowDelete } from '../shared/confirm.js';
import { flashId, keepScroll, revealRow, saveList, takeList } from '../shared/listnav.js';

export function languagesScreen(){
  const rows    = signal([]);
  const total   = signal(0);
  const page    = signal(1);
  const pages   = signal(1);
  const perPage = signal(DEFAULT_PER_PAGE);
  const loading = signal(true);
  let timer, lastQ = '';

  const formOpen = signal(false), formId = signal(null);
  const fCode = signal(''), fName = signal(''), fActive = signal(true);
  const formErr = signal(''), saving = signal(false), invalid = signal({});
  const confirmId = signal(null), deleting = signal(false), delErr = signal('');

  async function load(locateId){
    loading.value = true;
    const params = new URLSearchParams({ per_page: perPage.value, page: String(page.value) });
    if(lastQ && lastQ.trim()) params.set('q', lastQ.trim());
    if(locateId) params.set('locate', String(locateId));
    try { const r = await api.get('/admin/languages?' + params.toString());
          rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
          if(locateId && r.locate_page) page.value = r.locate_page; }
    catch { rows.value = []; total.value = 0; pages.value = 1; }
    loading.value = false;
  }
  const _saved = takeList('languages');   // returning from a language's Words? restore place (issue #24)
  if(_saved){ page.value = _saved.page || 1; perPage.value = _saved.perPage || DEFAULT_PER_PAGE; lastQ = _saved.q || ''; }
  load().then(() => {
    if(_saved && _saved.scroll) requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, _saved.scroll)));
  });
  function openWords(l){
    saveList('languages', { page: page.value, perPage: Number(perPage.value), q: lastQ });
    navigate('/languages/' + l.code);
  }
  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }

  function openCreate(){
    formId.value = null; fCode.value = ''; fName.value = ''; fActive.value = true;
    formErr.value = ''; invalid.value = {}; formOpen.value = true; focusModal();
  }
  function openEdit(l){
    formId.value = l.id; fCode.value = l.code || ''; fName.value = l.name || ''; fActive.value = l.is_active !== false;
    formErr.value = ''; invalid.value = {}; formOpen.value = true; focusModal();
  }
  const clearInvalid = k => { if(invalid.value[k]) invalid.value = { ...invalid.value, [k]: false }; };
  function closeForm(){ formOpen.value = false; }
  async function save(e){
    e.preventDefault();
    const inv = {};
    if(!fCode.value.trim()) inv.code = true;
    if(!fName.value.trim()) inv.name = true;
    invalid.value = inv;
    if(Object.keys(inv).length){ formErr.value = 'Please fill in the highlighted fields.'; return; }
    saving.value = true; formErr.value = '';
    const body = { code: fCode.value.trim(), name: fName.value.trim(), is_active: fActive.value };
    try {
      const editing = !!formId.value;
      const res = editing ? await api.put('/admin/languages/' + formId.value, body)
                          : await api.post('/admin/languages', body);
      const id = editing ? formId.value : (res && res.id);
      formOpen.value = false;
      await (editing ? keepScroll(() => load(), id) : revealRow(load, id));
    } catch(err){ formErr.value = (err.data && err.data.error) || err.message || 'Save failed'; }
    saving.value = false;
  }
  function armDelete(id){ delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try { await api.del('/admin/languages/' + id); confirmId.value = null; await load(); }
    catch(err){ delErr.value = (err.data && err.data.error) || err.message || 'Delete failed'; }
    deleting.value = false;
  }

  // Single reactive block: rows + pager together (a standalone pager slot appends — see pager.js).
  const list = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    if(!rows.value.length) return html`<div class="muted-block">No languages match.</div>`;
    const cId = confirmId.value, del = deleting.value, err = delErr.value;   // subscribe to delete state
    const hl = flashId.value;                                                // row to highlight
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    return html`<div class="list-wrap">
      ${pg()}
      <div class="row-list">${rows.value.map(l => { const blocked = cId === l.id && err; return html`
        <div class=${'row-card' + (l.is_active === false ? ' inactive' : '') + (hl === l.id ? ' row-hl' : '') + (blocked ? ' row-blocked' : '')}>
          <span class="iso-badge">${l.code}</span>
          <span class="row-main"><b>${l.name}</b>${l.is_active === false ? html`<span class="tag-off">inactive</span>` : ''}</span>
          ${blocked ? html`<span class="row-err">⚠ ${err}</span>` : null}
          <button class="btn btn-sm btn-outline" @click=${() => openWords(l)}>Words</button>
          <button class="btn btn-sm btn-outline cat-edit" title="Edit" @click=${() => openEdit(l)}>✎</button>
          ${blocked
            ? html`<button type="button" class="btn-confirm-no" @click=${cancelDelete}>OK</button>`
            : rowDelete({ armed: cId === l.id, busy: del, error: '',
                        onArm: () => armDelete(l.id), onCancel: cancelDelete, onDelete: () => doDelete(l.id) })}
        </div>`; })}</div>
      ${pg()}
    </div>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <h1 class="page-title">Languages</h1>
          <div class="page-sub">The locales the platform renders in — each country picks a default</div>
        </div>
        <button class="btn btn-primary" @click=${openCreate}>+ Add language</button>
      </div>
      <div class="toolbar">
        <input class="form-control" type="search" placeholder="Search code or name…" @input=${onSearch}>
        ${perPageSelect(perPage.value, onPer)}
      </div>
      <div class="count-line">${total} languages</div>
      ${list}

      <div class="modal-overlay" ?hidden=${() => !formOpen.value}>
        <form class="modal-card" @submit=${save}>
          <div class="modal-head">
            <h3>${() => formId.value ? 'Edit language' : 'Add language'}</h3>
            <button type="button" class="modal-x" @click=${closeForm}>✕</button>
          </div>
          <label class="form-label">Code</label>
          <input class=${() => 'form-control mb-2' + (invalid.value.code ? ' is-invalid' : '')} .value=${fCode} @input=${e => { fCode.value = e.target.value; clearInvalid('code'); }} placeholder="e.g. en or en-ZA">
          <label class="form-label">Name</label>
          <input class=${() => 'form-control mb-2' + (invalid.value.name ? ' is-invalid' : '')} .value=${fName} @input=${e => { fName.value = e.target.value; clearInvalid('name'); }} placeholder="e.g. English">
          <label class="form-check"><input type="checkbox" .checked=${fActive} @change=${e => { fActive.value = e.target.checked; }}> Active</label>
          <div class="form-note">Code is a locale — two letters, optionally a region (e.g. en, af, zu, en-ZA).</div>
          ${() => formErr.value ? html`<div class="alert alert-danger py-2 mb-2">${formErr}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeForm}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>`;

  return shell('languages', 'Languages', content);
}
